import { DateTime } from '@ksyos/date-time';
import { Button, Card, Col, Divider, Form, FormButtonRow, IconButton, Row, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ServiceType } from '@common/enums';
import type { ContractSettingGetAllApiResult } from '@common/interfaces/api/contractSettingsApi';
import type { IContractSettingAddEditValidated } from '@common/validation/contractSetting';
import type { FormResult } from '@frontend/utils/hooks/useForm';
import { ManagerAuthorization } from '@common/enums';
import { contractSettingAddEditFrontendSchema } from '@common/validation/contractSetting';
import { FormProvider } from '@frontend/components/contexts/form/FormProvider';
import { EmptyListPlaceholder } from '@frontend/components/elements/EmptyListPlaceholder';
import * as contractSettingsApiService from '@frontend/services/api/contractSettingsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { OrganizationContractSettingLogs } from '../OrganizationContractSettingLogs';
import { OrganizationContractSettingsAddFundingLink } from './OrganizationContractSettingsAddFundingLink';
import { OrganizationContractSettingFormColumnLabels } from './OrganizationContractSettingsFormColumnLabels';
import { OrganizationContractSettingsRow } from './OrganizationContractSettingsRow';
import { OrganizationContractSettingsTotalPricePerPatient } from './OrganizationContractSettingsTotalPricePerPatient';

interface IOrganizationContractSettingsProps {
    episodeCategoryId: string;
    organizationId: string;
    serviceTypes: ServiceType[];
}

type ContractSetting = ContractSettingGetAllApiResult['items'][0];

type NewContractSetting = Omit<ContractSetting, 'id' | 'contractPrice' | 'startDate' | 'created_at' | 'updated_at'> & {
    contractPrice: number | null;
    id?: string;
    startDate: string | Date | null;
};

export type OrganizationContractSettingsFormState = Record<ServiceType, IContractSettingAddEditValidated[]>;

export function OrganizationContractSettings(props: Readonly<IOrganizationContractSettingsProps>) {
    assertHasAuthorization([
        ManagerAuthorization.contractFundingDataAddEdit,
        ManagerAuthorization.organizationRead,
        ManagerAuthorization.organizationAddEdit,
    ]);

    const canAddEdit = hasOneAuthorization([ManagerAuthorization.contractFundingDataAddEdit]);

    const abortSignal = useAbortSignal();

    const [initialValuesSnapshot, setInitialValuesSnapshot] = useState<OrganizationContractSettingsFormState>(
        {} as OrganizationContractSettingsFormState
    );

    const [initialValues, setInitialValues] = useState<OrganizationContractSettingsFormState>(
        {} as OrganizationContractSettingsFormState
    );

    const [isEditing, setIsEditing] = useState(false);
    const [totalPricePerPatientPerQuarter, setTotalPricePerPatientPerQuarter] = useState(0);

    const translate = getSelectedLanguage();

    useEffect(() => {
        load({ episodeCategoryId: props.episodeCategoryId, organizationId: props.organizationId });
    }, [props.episodeCategoryId, props.organizationId]);

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    canAddEdit &&
                    !isEditing && (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setIsEditing(true)}
                        />
                    )
                }
                title={translate.contractSettings.subscription}
            />
            <Card.Body>
                {!initialValues && !isEditing ? (
                    <EmptyListPlaceholder
                        doNotWrapInCard={true}
                        subtitle={translate.contractSettings.emptyState}
                    />
                ) : (
                    <FormProvider
                        initialValues={initialValues}
                        onReset={handleReset}
                        onSubmit={handleSubmit}
                        validationSchema={contractSettingAddEditFrontendSchema}
                    >
                        {(form) => (
                            <Form
                                onReset={form.handleReset}
                                onSubmit={form.handleSubmit}
                                submitOnEnter={false}
                            >
                                <OrganizationContractSettingFormColumnLabels isEditing={isEditing} />
                                <Row>
                                    <Col span={12}>
                                        {Object.entries(form.values).map(([typeOfService, contractSettings]) => {
                                            const serviceType = typeOfService as ServiceType;

                                            return (
                                                <Row key={serviceType}>
                                                    <Col span={3}>
                                                        <Text
                                                            fontWeight="medium"
                                                            my={2}
                                                        >
                                                            {translate.serviceTypeEnum[serviceType]}
                                                        </Text>
                                                        {isEditing && canAddEdit && (
                                                            <OrganizationContractSettingsAddFundingLink
                                                                isDisabled={
                                                                    !contractSettings.at(0)?.startDate ||
                                                                    form.errors.hasErrorsForKeyWithArray(serviceType)
                                                                }
                                                                onClick={() =>
                                                                    handleAddContractSettingRecordForService(
                                                                        form,
                                                                        serviceType
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </Col>
                                                    <Col span={9}>
                                                        <Row rowGap={4}>
                                                            <OrganizationContractSettingsRow
                                                                contractSettings={contractSettings}
                                                                isEditing={isEditing}
                                                                serviceType={serviceType}
                                                            />
                                                        </Row>
                                                    </Col>
                                                    <Divider spacing="small" />
                                                </Row>
                                            );
                                        })}
                                    </Col>
                                </Row>
                                {isEditing ? (
                                    <FormButtonRow>
                                        <Button
                                            isDisabled={form.isSubmitting}
                                            type="reset"
                                            variant="outline"
                                        >
                                            {translate.common.cancel}
                                        </Button>
                                        <Button
                                            isDisabled={form.isSubmitting}
                                            onClick={form.validateForm}
                                            type="submit"
                                        >
                                            {translate.common.save}
                                        </Button>
                                    </FormButtonRow>
                                ) : (
                                    <OrganizationContractSettingsTotalPricePerPatient
                                        totalPerPatientPerQuarter={totalPricePerPatientPerQuarter}
                                    />
                                )}
                            </Form>
                        )}
                    </FormProvider>
                )}
            </Card.Body>

            {Object.keys(initialValues).length > 0 && !isEditing && (
                <Card.Footer>
                    <OrganizationContractSettingLogs
                        episodeCategoryId={props.episodeCategoryId}
                        organizationId={props.organizationId}
                    />
                </Card.Footer>
            )}
        </Card>
    );

    async function load({ episodeCategoryId, organizationId }: { episodeCategoryId: string; organizationId: string }) {
        const contractSettings = await contractSettingsApiService.getAll(
            { episodeCategoryId, organizationId },
            abortSignal
        );

        const contractSettingsOnServiceTypeMap = new Map<
            ServiceType,
            ContractSettingGetAllApiResult['items'] | NewContractSetting[]
        >();

        for (const serviceType of props.serviceTypes) {
            const existingContractSettings = contractSettings.items.filter((cs) => cs.serviceType === serviceType);

            contractSettingsOnServiceTypeMap.set(
                serviceType,
                existingContractSettings.length > 0
                    ? existingContractSettings
                    : [getEmptyContractSetting({ episodeCategoryId, organizationId, serviceType })]
            );
        }

        setInitialValuesSnapshot(
            Object.fromEntries(contractSettingsOnServiceTypeMap) as OrganizationContractSettingsFormState
        );
        setInitialValues(Object.fromEntries(contractSettingsOnServiceTypeMap) as OrganizationContractSettingsFormState);
        setTotalPricePerPatientPerQuarter(contractSettings.totalPricePerPatientPerQuarter);
    }

    function handleReset() {
        setInitialValues(initialValuesSnapshot);
        setIsEditing(false);
    }

    async function handleSubmit(values: OrganizationContractSettingsFormState) {
        const contractSettingsToUpdate = Object.values(values).flatMap((contractSettings) => contractSettings);

        await contractSettingsApiService.updateAll(contractSettingsToUpdate, abortSignal);

        await load({
            episodeCategoryId: props.episodeCategoryId,
            organizationId: props.organizationId,
        });

        setIsEditing(false);
    }

    function handleAddContractSettingRecordForService(
        form: FormResult<Record<ServiceType, IContractSettingAddEditValidated[]>>,
        serviceType: ServiceType
    ) {
        const lastActiveEndDate = form.values[serviceType].at(0)?.endDate;
        const nextActiveStartDate = lastActiveEndDate
            ? DateTime.from(lastActiveEndDate).add(1, 'month').startOf('month').toDate()
            : DateTime.now().startOf('month').toDate();

        const emptyContractSetting = getEmptyContractSetting({
            episodeCategoryId: props.episodeCategoryId,
            organizationId: props.organizationId,
            serviceType,
            startDate: nextActiveStartDate,
        });

        form.addArrayItem(
            serviceType,
            {
                ...emptyContractSetting,
                serviceType: emptyContractSetting.serviceType,
                showDeleteButton: true,
            },
            0
        );
    }

    function getEmptyContractSetting({
        episodeCategoryId,
        organizationId,
        serviceType,
        startDate,
    }: {
        episodeCategoryId: string;
        organizationId: string;
        serviceType: ServiceType;
        startDate?: Date;
    }): NewContractSetting {
        return {
            contractPrice: null,
            endDate: null,
            episodeCategoryId,
            organizationId,
            serviceType,
            startDate: startDate ? DateTime.from(startDate).format('YYYY-MM-DD') : null,
        };
    }
}
